import { undash } from 'pdfkit';
import { z } from 'zod';

declare global {
  interface Process {
    env: {
      LOG_LEVEL?: ILogLevel;
    };
  }
}

type EnvType = 'server' | 'browser';

export const ILogLevel = z.union([
  z.literal('DEBUG'),
  z.literal('INFO'),
  z.literal('WARN'),
  z.literal('ERROR'),
]);

export type ILogLevel = z.infer<typeof ILogLevel>;

export const LogLevel: Record<ILogLevel, ILogLevel> = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
};

const blackServerForeground = '\x1b[30m';

export const serverColorCodes: Record<
  string,
  { foreground: string; background: string }
> = {
  reset: {
    foreground: '\x1b[0m',
    background: '\x1b[0m' + blackServerForeground,
  },
  red: {
    foreground: '\x1b[31m',
    background: '\x1b[41m' + blackServerForeground,
  },
  yellow: {
    foreground: '\x1b[33m',
    background: '\x1b[43m' + blackServerForeground,
  },
  white: {
    foreground: '\x1b[37m',
    background: '\x1b[47m' + blackServerForeground,
  },
  gray: {
    foreground: '\x1b[90m',
    background: '\x1b[100m' + blackServerForeground,
  },
};

export const LogColor: Record<ILogLevel, keyof typeof serverColorCodes> = {
  DEBUG: 'white',
  INFO: 'white',
  WARN: 'yellow',
  ERROR: 'red',
};

const shouldColor = (envType: EnvType): boolean => {
  const isServer = envType === 'server';

  if (!isServer) {
    return false;
  }

  const doesntHaveNoColorSetToTrue =
    process.env.NO_COLOR === undefined || process.env.NO_COLOR !== 'true';

  return doesntHaveNoColorSetToTrue;
};

// Browser's color them already really nice and it's tricky to color the browser console in a dynamic way
const colorizeIfServer = ({
  logLevel,
  msg,
  envType,
  colorType,
}: {
  logLevel: ILogLevel;
  msg: unknown[];
  envType: EnvType;
  colorType: 'foreground' | 'background';
}): unknown[] => {
  if (shouldColor(envType)) {
    const color = serverColorCodes[LogColor[logLevel]][colorType];

    return [color, ...msg, serverColorCodes.reset.foreground];
  }

  return msg;
};

const ILogLevelRank = z.union([
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
type ILogLevelRank = z.infer<typeof ILogLevelRank>;

const LogLevelRank: Record<ILogLevel, ILogLevelRank> = {
  DEBUG: 2,
  INFO: 3,
  WARN: 4,
  ERROR: 5,
};

const defaultLogLevelRank = LogLevelRank.INFO;

/**
 * This checks for overrides in Node
 */
const processEnvLogLevelRank = (): null | ILogLevelRank => {
  try {
    if (
      typeof process !== 'undefined' &&
      typeof process.env?.LOG_LEVEL === 'string'
    ) {
      const logLevel = ILogLevel.parse(process.env.LOG_LEVEL);
      return LogLevelRank[logLevel];
    }

    return null;
  } catch (_e) {
    return null;
  }
};

const LOCAL_STORAGE_KEY = 'LOG_LEVEL';

/**
 * This checks for overrides in the browser's localStorage
 */
const windowLogLevelRank = (): null | ILogLevelRank => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage?.getItem(LOCAL_STORAGE_KEY)
    ) {
      const logLevel = ILogLevel.parse(
        window.localStorage.getItem(LOCAL_STORAGE_KEY),
      );
      return LogLevelRank[logLevel];
    }

    return null;
  } catch (_e) {
    return null;
  }
};

/**
 * This checks for overrides in both Node and in the browser.
 * If none are found, it defaults to INFO
 */
const isomorphicLogLevelRank = (
  constructorLogLevel?: ILogLevel,
): ILogLevelRank => {
  const constructorLogLevelRank: null | ILogLevelRank =
    (constructorLogLevel && LogLevelRank[constructorLogLevel]) || null;

  return (
    windowLogLevelRank() ??
    processEnvLogLevelRank() ??
    constructorLogLevelRank ??
    defaultLogLevelRank
  );
};

const shouldLog = ({
  loggerRank,
  logFnRank,
}: {
  loggerRank: ILogLevelRank;
  logFnRank: ILogLevelRank;
}): boolean => {
  return logFnRank >= loggerRank;
};

const prefix = ({
  name,
  logLevel,
}: {
  name?: string;
  logLevel: ILogLevel;
}): string => {
  if (name) {
    return `${LogLevel[logLevel]} [${name}]:`;
  }

  return `${LogLevel[logLevel]}:`;
};

interface LoggerOptions {
  name?: string;
  level?: ILogLevel;
  onLog?: (logLevel: ILogLevel, ...msg: unknown[]) => void;
}

export interface LoggerPayload {
  count: (label: string) => void;
  debug: (...msg: unknown[]) => void;
  info: (...msg: unknown[]) => void;
  warn: (...msg: unknown[]) => void;
  error: (...msg: unknown[]) => void;

  json: {
    debug: (msg: unknown) => void;
    info: (msg: unknown) => void;
    warn: (msg: unknown) => void;
    error: (msg: unknown) => void;
  };
}

const doLog = ({
  logLevel,
  envType,
  msg,
  logFnRank,
  loggerRank,
  onLog,
  name,
  logFn,
}: {
  logLevel: ILogLevel;
  logFnRank: ILogLevelRank;
  loggerRank: ILogLevelRank;
  msg: unknown[];
  envType: EnvType;
  name?: string;
  onLog?: (logLevel: ILogLevel, ...msg: unknown[]) => void;
  logFn: (...msg: unknown[]) => void;
}): void => {
  if (shouldLog({ loggerRank, logFnRank })) {
    if (onLog) {
      onLog(logLevel, ...msg);
    }

    if (envType === 'server') {
      logFn(
        ...colorizeIfServer({
          msg: [prefix({ name, logLevel })],
          logLevel,
          envType,
          colorType: 'background',
        }),
        ...colorizeIfServer({
          colorType: 'foreground',
          msg,
          logLevel,
          envType,
        }),
      );
    } else {
      logFn(
        ...colorizeIfServer({
          colorType: 'foreground',
          msg: [prefix({ name, logLevel }), ...msg],
          logLevel,
          envType,
        }),
      );
    }
  }
};

const doJsonLog = ({
  logLevel,
  envType,
  msg,
  logFnRank,
  loggerRank,
  onLog,
  name,
  logFn,
}: {
  logLevel: ILogLevel;
  logFnRank: ILogLevelRank;
  loggerRank: ILogLevelRank;
  msg: unknown;
  envType: EnvType;
  name?: string;
  onLog?: (logLevel: ILogLevel, msg: unknown) => void;
  logFn: (...msg: unknown[]) => void;
}): void => {
  if (shouldLog({ loggerRank, logFnRank })) {
    const json = JSON.stringify(msg, null, 2);

    if (onLog) {
      onLog(logLevel, json);
    }

    if (envType === 'server') {
      logFn(
        ...colorizeIfServer({
          msg: [prefix({ name, logLevel })],
          logLevel,
          envType,
          colorType: 'background',
        }),
        ...colorizeIfServer({
          colorType: 'foreground',
          msg: [json],
          logLevel,
          envType,
        }),
      );
    } else {
      logFn(
        ...colorizeIfServer({
          colorType: 'foreground',
          msg: [prefix({ name, logLevel }), json],
          logLevel,
          envType,
        }),
      );
    }
  }
};

/**
 * Isomorphic logger for Node.js and the browser
 * Can be overriden either through process.env.LOG_LEVEL or window.localStorage with the key 'LOG_LEVEL'
 * or with the instance from it created by createLogger
 *
 * Defaults to LogLevel.INFO
 *
 * @example const logger = createLogger(); logger.info("Hello, World!") // INFO: Hello, World!
 * @example const logger = createLogger({name: "My Module"}); logger.info("Hello, World!") // INFO [My Module]: Hello, World!
 * @example const logger = createLogger(); logger.info("Hello, World!") // prints nothing since logLevel is set to LogLevel.ERROR
 * @example const logger = createLogger({name: "My Module"}); logger.json.info({someProp: "Hello, World!"}) // INFO [My Module] { "someProp": "Hello, World!"}
 * @example const logger = createLogger({name: "My Module"}); logger.count("Hello, World!") // [My Module]: Hello, World!: 1
 * @example const logger = createLogger(); logger.count("Hello, World!") // Hello, World!: 1
 */
export const createLogger = (options?: LoggerOptions): LoggerPayload => {
  const loggerRank = isomorphicLogLevelRank(options?.level);
  const name = options?.name;
  const onLog = options?.onLog;

  const envType = typeof window === 'undefined' ? 'server' : 'browser';

  return {
    count(label) {
      const value = name ? `[${name}]: ${label}` : label;

      console.count(value);
    },

    debug(...msg) {
      doLog({
        logFnRank: LogLevelRank.DEBUG,
        logLevel: LogLevel.DEBUG,
        loggerRank,
        envType,
        msg,
        onLog,
        logFn: console.info,
        name,
      });
    },

    info(...msg) {
      doLog({
        logFnRank: LogLevelRank.INFO,
        logLevel: LogLevel.INFO,
        loggerRank,
        envType,
        msg,
        onLog,
        logFn: console.info,
        name,
      });
    },

    warn(...msg) {
      doLog({
        logFnRank: LogLevelRank.WARN,
        logLevel: LogLevel.WARN,
        loggerRank,
        envType,
        msg,
        onLog,
        logFn: console.warn,
        name,
      });
    },

    error(...msg) {
      doLog({
        logFnRank: LogLevelRank.ERROR,
        logLevel: LogLevel.ERROR,
        loggerRank,
        envType,
        msg,
        onLog,
        logFn: console.error,
        name,
      });
    },

    json: {
      debug(msg) {
        doJsonLog({
          logFnRank: LogLevelRank.DEBUG,
          logLevel: LogLevel.DEBUG,
          loggerRank,
          envType,
          msg,
          onLog,
          logFn: console.info,
          name,
        });
      },

      info(msg) {
        doJsonLog({
          logFnRank: LogLevelRank.INFO,
          logLevel: LogLevel.INFO,
          loggerRank,
          envType,
          msg,
          onLog,
          logFn: console.info,
          name,
        });
      },

      warn(msg) {
        doJsonLog({
          logFnRank: LogLevelRank.WARN,
          logLevel: LogLevel.WARN,
          loggerRank,
          envType,
          msg,
          onLog,
          logFn: console.warn,
          name,
        });
      },

      error(msg) {
        doJsonLog({
          logFnRank: LogLevelRank.ERROR,
          logLevel: LogLevel.ERROR,
          loggerRank,
          envType,
          msg,
          onLog,
          logFn: console.error,
          name,
        });
      },
    },
  };
};
